import React from 'react';
import {useState, useCallback, FormEvent} from 'react';
import {
  chakra,
  Box,
  Stack, VStack, HStack,
  Center,
  Container,
  Spacer,
  Heading,
  Image,
  Text,
  Button,
  Input, Checkbox, InputProps,
  Collapse
} from '@chakra-ui/react';
import { aggregateData } from '../aggregateData';
import {IMAGE_PREFIX} from '../env';

function request(method: string, uri: string, data: FormData) {
  return new Promise((resolve, reject) => {
    const req = new XMLHttpRequest();
    req.onload = resolve;
    req.onerror = reject;
    req.open(method, uri);
    req.send(data);
  });
}

type Props = {
  estates: Estate[]
};

export default function NewsLetter({estates}: Props) {
  const [email, setEmail] = useState('');
  const [formState, setFormState] = useState<'initial' | 'sending' | 'successful' | 'failed'>('initial');
  const sendTrial = useCallback(async (event: FormEvent) => {
    setFormState('sending');
    event.preventDefault();
    const estatesWithChart = aggregateData(estates);
    const form = event.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const charts = await Promise.allSettled(estatesWithChart.map(({chart}) => chart));
    for (let i = 0, l = estatesWithChart.length; i < l; i++) {
      const {name, dividuals} = estatesWithChart[i];
      const chart = charts[i];
      formData.append('estates[][name]', name);
      if (chart.status === 'fulfilled') {
        formData.append('estates[][chart]', chart.value) ;
      }
      for (let {name: n, ratio, comfort} of dividuals) {
        formData.append('estates[][dividuals][][name]', n);
        formData.append('estates[][dividuals][][ratio]', ratio.toString());
        formData.append('estates[][dividuals][][comfort]', comfort.toString());
      }
    }

    request(form.method, form.action, formData)
      .then(() => setFormState('successful'))
      .catch(err => {
        setFormState('failed');
        console.error(err);
      });
  }, [estates, setFormState]);

  return (
    <chakra.section mt={[`8.5rem`, `8.5rem`, `${251/16}rem`]} position="relative">
      <Box
        role="presentation"
        display={formState === 'successful' ? 'none' : 'block'}
        position="absolute"
        top="0"
        left="50%"
        transform={['translate(-50%, -10.933333333333334vw)', 'translate(-50%, -10.933333333333334vw)', "translate(-50%, -7.34375vw)"]}
        w={['26.666666666666668vw', '26.666666666666668vw', "15.3125vw"]}
      >
        <Image src={`${IMAGE_PREFIX}img-graph-05.png`} alt="" />
      </Box>
      <Container maxW={['contentSm', 'contentSm', "content"]} p="0">
        <Collapse in={formState !== 'successful'}>
          <Box pt={['4rem', '4rem', "24"]} pb="16" bgColor="blue.100" borderRadius="2xl" textAlign="center">
            <Heading w={['88.05970149253731%', '88.05970149253731%', 'full']} mx="auto" letterSpacing="0.1em">
              生成した
              <chakra.span fontFamily="inherit" fontWeight="inherit" color="blue.500">“わたしの分人” 円グラフ</chakra.span>
              を
              <chakra.br display={['none', 'none', 'inline']} />
              メールで受け取る
            </Heading>
            <chakra.form
              method="post"
              action="https://asia-northeast1-dividualism-k-hirano-com-f.cloudfunctions.net/send-trial"
              encType="multipart/form-data"
              onSubmit={sendTrial}
              mt={['1.875rem', '1.875rem', "12"]}
              sx={{
                '.btn-large button': {
                  border: 'none',
                  boxShadow: 'none',
                  color: formState === 'sending' ? 'inherit' : 'white'
                }
              }}
            >
              <VStack spacing={[7, 7, 8]} w={['88.05970149253731%', '88.05970149253731%', "61.462450592885375%"]} mx="auto">
                <FormInput
                  label="お名前"
                  name="name"
                  id="newsletter-name"
                />
                <FormInput
                  label="メールアドレス"
                  name="email"
                  type="email"
                  id="newsletter-email"
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                />
              </VStack>
              <HStack w={['88.05970149253731%', '88.05970149253731%', "61.462450592885375%"]} mt="6" mx="auto" justify="space-between">
                <Spacer flex="2" mr="3" display={['none', 'none', 'block']} />
                <chakra.output
                  htmlFor="newsletter-email"
                  fontSize="1.5rem"
                  flex="3"
                  p={[0, 0, "15px"]}
                  lineHeight="1"
                  textAlign="start"
                >{email}</chakra.output>
              </HStack>
              <HStack mt="8" spacing={[1, 1, "9"]} justify={['flex-start', 'flex-start', "center"]} fontSize="1rem" w={['88.05970149253731%', '88.05970149253731%', "61.462450592885375%"]} mx="auto">
                <Checkbox required checked id="newsletter-policy-agreement" sx={{
                  '.chakra-checkbox__control:not([data-checked])': {
                    bgColor: 'white'
                  }
                }} />
                <label htmlFor="newsletter-policy-agreement"><chakra.a href="https://corkagency.com/privacy" target="privacy" display="inline" textDecoration="underline">プライバシーポリシー</chakra.a>に同意して</label>
              </HStack>
              <Center className="btn-large" mt="1.375rem">
                <Button
                  type="submit"
                  disabled={formState === 'sending'}
                  h="auto"
                  bgColor={formState === 'sending' ? 'gray.100' : 'blue.500'}
                  _after={{display: 'none'}}
                >{formState === 'sending' ? '送信中' : '上記内容で送信する'}</Button>
              </Center>
              <Box fontSize="1rem" w={['88.05970149253731%', '88.05970149253731%', "61.462450592885375%"]} mx="auto" mt={[4, 4, 8]} color="gray.500" textAlign="start" lineHeight={[1.5, 1.5, 2]}>
                <Text>※ご提供いただいた個人情報は、メール配信以外の目的では利用いたしません。</Text>
                <Text>また、ご本人の同意なく個人情報を第三者に提供することはございません。</Text>
              </Box>
            </chakra.form>
          </Box>
        </Collapse>
        <Collapse in={formState === 'successful'}>
          <Box textAlign="center">
            <Heading letterSpacing="0.1em" color="blue.500">送信が完了しました！</Heading>
            <Box mt="1em">
              <Text fontWeight="bold">分人体験お疲れさまでした！</Text>
              <Text fontWeight="bold">ご登録いただいたアドレスにメールをお送りしましたので、ご確認ください。</Text>
            </Box>
          </Box>
        </Collapse>
      </Container>
    </chakra.section>
  );
}

type FormInputProps = InputProps & {
  label: string,
  id: string
};

function FormInput({label, id, ...inputProps}: FormInputProps) {
  return (
    <Stack
      direction={['column', 'column', 'row']}
      justify="space-between"
      align="center"
      fontSize={['lg', 'lg', "1.5rem"]}
      spacing={[1, 1, 0]}
      w="full"
      lineHeight="1"
      textAlign="start"
    >
      <chakra.label
        htmlFor={id}
        fontSize="inherit"
        flex="2"
        w={['full', 'full', 'auto']}
        color="blue.500"
      >{label}</chakra.label>
      <Input
        id={id}
        required
        flex="3"
        fontSize="inherit"
        h="54px"
        p="15px"
        lineHeight="1"
        bgColor="white"
        {...inputProps}
      />
    </Stack>
  );
}
